import { useCallback, useEffect } from 'react';
import { CommonService } from '@/services';
import {useDispatch} from "react-redux";
import {updateProjectDetails} from "@/store/actions/projectdata.action.ts";
import commonService from '@/services/common.service.ts';
import {useSocket} from "@/services/hooks/useSocket.ts";
import { useQueryClient } from "@tanstack/react-query";
import { projectsListKey } from "@/screens/app/projects-list/ProjectsComponent";

enum DBEventType {
    DBGenerated = "agent.nextjs.sql.generated",
}

enum SocketErrorEventType {
    SOCKET_ERROR = "socket.error",
}

enum AgentNextjsDeltaEventType {
    AGENT_NEXTJS_DELTA = "agent.nextjs.delta",
}



export const useChatSocket = (updateLoadingMessage:(message:any, isStreaming?: boolean)=>void, projectId: string, handleErrorMessage: (errorMessage?: string) => void,useSocketForMessage?: boolean) => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const { events ,clearEvent} = useSocket({
        eventTypes: [
            DBEventType.DBGenerated,
            SocketErrorEventType.SOCKET_ERROR,
            AgentNextjsDeltaEventType.AGENT_NEXTJS_DELTA            
        ],
        autoConnect: true,
        persist: true,
        projectId: projectId || "",
        logTag: 'PreviewRenderer',
        debug: false, // Set to true only during development for detailed logs
    });

    const handleStreamedEvent = useCallback((event_type: string, data: any) => {
        switch (event_type) {
            case "meta_data": {
                const appName = data?.meta_data?.app_name;
                if (appName && projectId) {
                    dispatch(updateProjectDetails({
                        "name": appName
                    }))
                    
                    // Update projects list cache directly with new name
                    queryClient.setQueryData([projectsListKey], (oldData: any) => {
                        if (!oldData || !Array.isArray(oldData)) return oldData;
                        
                        return oldData.map((project: any) => 
                            project.id === projectId 
                                ? { ...project, name: appName }
                                : project
                        );
                    });
                }
                break;
            }
            case "scratch_pad_delta":
                // Append scratch pad delta
                updateLoadingMessage({
                    extra: {
                        scratchPad: data?.delta || ''
                    }
                } as any, true);
                break;
            case "description_delta":
                updateLoadingMessage({
                    message: data?.delta || "",
                }, true);
                break;
            case "file_start": {
                const path = data?.path;
                if (path) {
                    updateLoadingMessage({
                        extra: {
                            files: {
                                [path]: {
                                    line_count: 0,
                                    file_hash: ''
                                }
                            }
                        }
                    }, true);
                }
                break;
            }

            case "file_delta": {
                const path = data?.path;
                const delta = data?.delta || "";
                if (path && delta !== undefined) {
                    updateLoadingMessage({
                        extra: {
                            files: {
                                [path]: {
                                    line_count: delta.split('\n').length - 1,
                                    file_hash: ''
                                }
                            }
                        }
                    }, true);
                }
                break;
            }

            case "file_end": {
                const path = data?.path;
                if (path) {
                    updateLoadingMessage({
                        extra: {
                            files: {
                                [path]: {
                                    line_count: 0,
                                    file_hash: data?.file_hash || ''
                                }
                            }
                        }
                    }, true);
                }
                break;
            }

            case "model_usage":
                updateLoadingMessage({
                    id: Date.now(),
                    role: 'agent',
                    message: '',
                    extra: {
                        model_usage: data
                    }
                }, true);
                break;

            default:
                console.warn("Unknown event:", event_type);
        }
    }, [updateLoadingMessage, dispatch, queryClient, projectId]);


    const handleStreamFromSocket = useCallback((event_type: string, data: any) => {
        switch (event_type) {
            case AgentNextjsDeltaEventType.AGENT_NEXTJS_DELTA:
                updateLoadingMessage({
                    message: data?.description || '',
                    extra: {
                        scratchPad: data?.scratch_pad || '',
                    }
                } as any, false);
                break;          
            default:
                console.warn("Unknown event:", event_type);
        }
        
    }, [updateLoadingMessage]);

    // Process RxJS stream chunks
    const processChunk = useCallback((chunk: string) => {
        try {
            const message = commonService.processStream(chunk);

            const eventType = message?.type;
            const eventData = message?.data;

            if (eventType && !useSocketForMessage) {
                handleStreamedEvent(eventType, eventData);
            }
        } catch (err) {
            console.error("Error processing chunk:", chunk, err);
        }
    }, [handleStreamedEvent, useSocketForMessage]);

    useEffect(() => {
        const dbEvent = events[DBEventType.DBGenerated]
        const agentNextjsDeltaEvent = events[AgentNextjsDeltaEventType.AGENT_NEXTJS_DELTA];
        if (dbEvent) {
            updateLoadingMessage({
                extra: {
                    migration_id:dbEvent?.event_meta?.migration_id,
                    file_path:dbEvent?.event_meta?.file_content,
                    db_query_result:false,
                }
            })
            clearEvent(DBEventType.DBGenerated);
        }
        else if (agentNextjsDeltaEvent) {
            handleStreamFromSocket(AgentNextjsDeltaEventType.AGENT_NEXTJS_DELTA,agentNextjsDeltaEvent?.event_meta);
        }

    }, [events,updateLoadingMessage,handleStreamFromSocket,clearEvent]);

    useEffect(() => {
        const errorEvent = events[SocketErrorEventType.SOCKET_ERROR];
        if (errorEvent) {
            const reason = errorEvent.event_meta?.extra?.reason;
            if (reason && useSocketForMessage) {
                handleErrorMessage(reason);
            }
        }
    }, [events, handleErrorMessage,useSocketForMessage]);

    // RxJS Subscriptions
    useEffect(() => {
        const sub = CommonService._communications.handleCodeStreamerSubject.subscribe(processChunk);
        return () => sub.unsubscribe();
    }, [processChunk]);
};